import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { cancelRenewTrialPeriod, isRebranding } from 'reactApp/appHelpers/featuresHelpers';
import {
    discountInOutflowSaveSegments,
    isSaveDiscountInOutlowEnabled,
} from 'reactApp/appHelpers/featuresHelpers/features/discountInOutflow';
import { BuySubscriptionActions, BuySubscriptionSelectors } from 'reactApp/modules/buySubscription/buySubscription.module';
import { getFeatureAbCancelSubsWithCaptcha } from 'reactApp/modules/features/features.selectors';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import type { OutflowOfferMobileProps, OutflowOfferWebProps } from 'reactApp/modules/subscriptionRenewal/outflowOffers/offers.types';
import { checkDiscountOffer } from 'reactApp/modules/subscriptionRenewal/outflowOffers/offers/discount.offer';
import { SUBSCRIPTION_ANALYTICS } from 'reactApp/sections/MobileSubscriptionsPage/MobileSubscription.analytics';
import { ESubscriptionModalAction } from 'reactApp/sections/SubscriptionsPage/ui/SubscriptionModal/SubscriptionModal.analytics';
import { store } from 'reactApp/store';
import { renderCancelSubscriptionWithCaptchaModal } from 'reactApp/ui/CancelSubscriptionWithCaptchaModal/CancelSubscriptionWithCaptchaModal.helpers';
import {
    type DiscountInOutflowLSData,
    DISCOUNT_IN_OUTFLOW,
    discountInOutflowLogger,
} from 'reactApp/ui/DiscountInOutflow/DiscountInOutflow.const';
import { findLowerBound } from 'reactApp/utils/findLowerBound';
import { sendDwh } from 'reactApp/utils/ga';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { daysToSeconds, secondsToDaysFloor } from 'reactApp/utils/timeHelpers';
import { put } from 'redux-saga/effects';
import { select } from 'typed-redux-saga';

export function* captchaAndSurvey({ subscription, product, sendAnalytics }: OutflowOfferWebProps) {
    const showDiscountInOutflow = yield checkDiscountOffer({ subscription, product });
    const discountInOutflow = yield* select(BuySubscriptionSelectors.getDiscountInOutflow);
    // tempexp_18907-start
    const onSuccessCancelAutorenew = () => {
        const cancelDate = Date.now();
        const expirationDate: number = subscription?.expires || 0;
        const remainingSeconds = (expirationDate - cancelDate) / 1000;
        const remainingDays = secondsToDaysFloor(remainingSeconds);
        const period = findLowerBound(discountInOutflowSaveSegments, remainingDays);

        if (showDiscountInOutflow && isSaveDiscountInOutlowEnabled && period !== null) {
            discountInOutflowLogger.info(`calculated period - ${period}`);

            store.dispatch(BuySubscriptionActions.setDiscountInOutflowSave({ status: true, period }));
            discountInOutflowLogger.info(`set LS`);
            storeHelper.setValue(DISCOUNT_IN_OUTFLOW, {
                subId: subscription.id,
                expirationDate: cancelDate + daysToSeconds(period) * 1000,
                originalPrice: product.price,
                period: product.period,
                discountPrice: discountInOutflow,
            } as DiscountInOutflowLSData);
            emitAnalyticEvent(AnalyticEventNames.DISCOUNT_LS_SAVE);
        }
        sendPaymentGa({
            action: ESubscriptionModalAction.cancelAutopaymentSuccess,
            eventCategory: ECategoryGa.payment,
            remainingSeconds: Math.floor(remainingSeconds),
        });
        emitAnalyticEvent(AnalyticEventNames.CANCEL_SUB_SUCCESS);
    };
    // tempexp_18907-end

    // tempexp_13984-start
    const abWithCaptcha = yield* select(getFeatureAbCancelSubsWithCaptcha);
    if (abWithCaptcha === 'b') {
        yield renderCancelSubscriptionWithCaptchaModal({ subscriptionId: subscription.id, isRebranding, onSuccessCancelAutorenew });
    }
    // tempexp_13984-end
    else {
        yield put(
            BuySubscriptionActions.cancelAutorenewSubscription({
                subscriptionId: subscription.id,
                sendAnalytics,
                // tempexp_18907-next-line
                onSuccess: onSuccessCancelAutorenew,
            })
        );
    }
}

export function* captchaAndSurveyMobile({ dwhData, subscription, isRebranding, isRebrandingQuotaLanding }: OutflowOfferMobileProps) {
    // tempexp_13984-next-line
    const abWithCaptcha = yield* select(getFeatureAbCancelSubsWithCaptcha);

    const sendAnalytics = (action: ESubscriptionModalAction) => {
        sendDwh({
            ...SUBSCRIPTION_ANALYTICS.CANCEL_AUTOPAY_RESTORE,
            action: `my-tariff-${action}`,
            dwhData,
        });
    };

    // tempexp_13984-start
    if (abWithCaptcha === 'b') {
        renderCancelSubscriptionWithCaptchaModal({
            subscriptionId: subscription.id,
            isPhone: true,
            isRebrandingQuotaLanding,
            isRebranding,
        });
        return;
    }
    // tempexp_13984-end

    // tempexp_16480-next-line
    if (cancelRenewTrialPeriod) {
        store.dispatch(
            BuySubscriptionActions.cancelAutorenewCheckCaptchaAndTrial({
                subscriptionId: subscription.id,
                sendAnalytics,
                period: cancelRenewTrialPeriod,
            })
        );
    } else {
        store.dispatch(BuySubscriptionActions.cancelAutorenewSubscription({ subscriptionId: subscription.id, sendAnalytics }));
    }
}
